import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { be1Fetch } from '../../config/api';
import { clearRunStatus, setRunDurations, setRunRows, setRunStatus } from '../../store/workflow/workflowSlice';
import { computeNodeDurations, computeNodeRows, computeNodeRunStatus } from './utils/runStatus';
import { withTimeZone } from '../../utils/timeZone';

/** 서버에 다시 묻기 전 대기 — 연속 편집(노드 여러 개 붙여 넣기, 워크플로 열기 중 노드 주입)을 한 번으로 모은다 */
const DEBOUNCE_MS = 800;
/** 최종 실행이 아직 돌고 있으면 이 간격으로 다시 묻는다 */
const RUNNING_POLL_MS = 5000;

/**
 * 노드별 최종 실행 상태·소요시간을 캔버스에 맞춘다 — 레이아웃에 하나만 둔다(화면 없음).
 *
 * 워크플로를 열 때, 워크플로를 바꿀 때, 실행이 끝났을 때(openflow:run-finished) 서버(/api/canvas/last-run)에 묻는다.
 * 서버가 stale(최종 실행 뒤에 워크플로가 바뀜)이라고 하면 상태를 비운다.
 * 바뀜 판정은 DAG 기준이라 노드 위치만 옮기거나 선택한 것은 바뀐 것으로 치지 않는다 — 그래서 여기서도 그런 변화에는 묻지 않는다.
 */
const RunStatusSync: React.FC = () => {
  const dispatch = useDispatch();
  const currentId = useSelector((s: any) => s.workflow.current?.id as string | undefined);
  const currentName = useSelector((s: any) => s.workflow.current?.name as string | undefined);
  const nodes = useSelector((s: any) => s.workflow.nodes as any[]);
  const edges = useSelector((s: any) => s.workflow.edges as any[]);
  const settings = useSelector((s: any) => s.workflow.settings);

  // 요청 시점의 최신 캔버스 — 타이머·이벤트에서 부를 때 오래된 값을 보내지 않도록
  const latest = useRef({ currentId, currentName, nodes, edges, settings });
  latest.current = { currentId, currentName, nodes, edges, settings };
  const seq = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sync = useCallback(async () => {
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    const { currentId: id, currentName: name, nodes: ns, edges: es, settings: st } = latest.current;
    if (!id) return;
    const my = ++seq.current;
    try {
      const r = await be1Fetch('/api/canvas/last-run', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow_id: id, name, nodes: ns, edges: es, schedule_interval: st?.schedule, settings: withTimeZone(st) }),
      }, { retries: 0 });
      if (!r.ok) return;                       // 표시를 못 할 뿐 편집에는 지장이 없다 — 알림을 띄우지 않는다
      const j = await r.json();
      if (my !== seq.current || latest.current.currentId !== id) return;  // 그 사이 더 새 요청이 나갔거나 다른 워크플로를 열었다
      if (!j.run || j.stale) { dispatch(clearRunStatus()); return; }
      const taskMap = j.task_map || {};
      const tasks = j.tasks || [];
      dispatch(setRunStatus(computeNodeRunStatus(ns, es, taskMap, tasks, j.run.state)));
      dispatch(setRunDurations(computeNodeDurations(taskMap, tasks)));
      // 건수는 태스크가 남긴 값에서 따로 읽는다 — 상태보다 무거운 조회라 실행이 바뀔 때만 부른다
      try {
        const rr = await be1Fetch(`/api/canvas/${j.dag_id}/runs/${encodeURIComponent(j.run.dag_run_id)}/rows`, undefined, { retries: 0 });
        if (rr.ok && my === seq.current) {
          const rows = (await rr.json()).rows || {};
          dispatch(setRunRows(computeNodeRows(ns, es, taskMap, rows)));
        }
      } catch { /* 건수를 못 읽어도 상태 표시는 그대로 쓴다 */ }
      if (j.run.state === 'running' || j.run.state === 'queued') {
        pollTimer.current = setTimeout(() => { sync(); }, RUNNING_POLL_MS);
      }
    } catch { /* 네트워크 오류 — 다음 편집이나 실행 때 다시 묻는다 */ }
  }, [dispatch]);

  // DAG 에 영향이 있는 것만 모은 서명 — 위치·선택·크기 변화로는 바뀌지 않는다
  const signature = useMemo(() => JSON.stringify({
    id: currentId,
    n: (nodes || []).map((n) => [n.id, n.type, n.parentId || null, n.data?.label, n.data?.config, n.data?.customId]),
    e: (edges || []).map((e) => [e.source, e.target, e.sourceHandle || null, e.targetHandle || null]),
    s: settings,
  }), [currentId, nodes, edges, settings]);

  useEffect(() => {
    if (!currentId) return;
    const t = setTimeout(() => { sync(); }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [signature, currentId, sync]);

  useEffect(() => {
    const h = () => { sync(); };
    window.addEventListener('openflow:run-finished', h);
    return () => {
      window.removeEventListener('openflow:run-finished', h);
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [sync]);

  return null;
};

export default RunStatusSync;
